import React, { useState, useContext } from 'react';
import { 
  Trophy, 
  Target, 
  TrendingUp, 
  Award, 
  Star,
  Crown,
  Medal,
  Zap,
  Calendar,
  Users,
  Plus,
  Edit,
  BarChart3,
  Clock,
  CheckCircle,
  Flame,
  Gift
} from 'lucide-react';
import { useAuth } from '../App';

const Challenges = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  
  // Données d'exemple pour les challenges
  const [challenges] = useState([
    {
      id: 1,
      title: 'Champion du Mois',
      description: 'Réalisez le plus de ventes ce mois-ci',
      type: 'monthly',
      startDate: '2025-01-01',
      endDate: '2025-01-31',
      reward: 'Bonus 500€ + Journée de congé',
      status: 'active',
      participants: 8,
      myPosition: 2,
      myProgress: 75,
      target: 10,
      achieved: 7.5,
      leaderboard: [
        { name: 'Marie Dubois', sales: 12, avatar: '👩' },
        { name: 'Vous', sales: 10, avatar: '👤' },
        { name: 'Pierre Martin', sales: 8, avatar: '👨' },
        { name: 'Julie Bernard', sales: 7, avatar: '👩‍🦰' }
      ]
    },
    {
      id: 2,
      title: 'Prospection Excellence',
      description: 'Générez 50 nouveaux prospects en 2 semaines',
      type: 'sprint',
      startDate: '2025-01-15',
      endDate: '2025-01-29',
      reward: 'iPad Pro',
      status: 'active',
      participants: 12,
      myPosition: 4,
      myProgress: 60,
      target: 50,
      achieved: 30,
      leaderboard: [
        { name: 'Commercial A', prospects: 45, avatar: '🔥' },
        { name: 'Commercial B', prospects: 38, avatar: '⭐' },
        { name: 'Commercial C', prospects: 35, avatar: '🎯' },
        { name: 'Vous', prospects: 30, avatar: '👤' }
      ]
    },
    {
      id: 3,
      title: 'Roi de la Signature',
      description: 'Signez 20 devis ce trimestre',
      type: 'quarterly',
      startDate: '2025-01-01',
      endDate: '2025-03-31',
      reward: 'Week-end Spa + 1000€',
      status: 'upcoming',
      participants: 15,
      myPosition: null,
      myProgress: 0,
      target: 20,
      achieved: 0
    }
  ]);

  // Statistiques globales pour les admins
  const [globalStats] = useState({
    totalChallenges: 8,
    activeChallenges: 3,
    totalParticipants: 25,
    averageEngagement: 78,
    topPerformer: 'Marie Dubois',
    monthlyGrowth: 15
  });

  const getStatusColor = (status) => {
    switch(status) {
      case 'active': return 'bg-green-100 text-green-800 border-green-200';
      case 'upcoming': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'completed': return 'bg-gray-100 text-gray-800 border-gray-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusLabel = (status) => {
    switch(status) {
      case 'active': return 'En cours';
      case 'upcoming': return 'À venir';
      case 'completed': return 'Terminé';
      default: return 'Inconnu';
    }
  };

  const getProgressColor = (progress) => {
    if (progress >= 80) return 'bg-green-500';
    if (progress >= 60) return 'bg-yellow-500';
    if (progress >= 40) return 'bg-orange-500';
    return 'bg-red-500';
  };

  const getPositionIcon = (position) => {
    switch(position) {
      case 1: return <Crown className="w-5 h-5 text-yellow-500" />;
      case 2: return <Medal className="w-5 h-5 text-gray-400" />;
      case 3: return <Award className="w-5 h-5 text-orange-600" />;
      default: return <Trophy className="w-5 h-5 text-gray-400" />;
    }
  };

  // Vue Admin
  if (isAdmin) {
    return (
      <div className="p-6 space-y-6">
        {/* Header Admin */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl flex items-center justify-center">
              <Trophy className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Challenges - Vue d'ensemble</h1>
              <p className="text-gray-600">Gestion et suivi des défis commerciaux</p>
            </div>
          </div>
          
          <button className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl font-medium hover:from-purple-700 hover:to-indigo-700 transition-all duration-200">
            <Plus className="w-4 h-4" />
            Créer un Challenge
          </button>
        </div>

        {/* Statistiques globales */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                <Target className="w-4 h-4 text-blue-600" />
              </div>
              <h3 className="font-semibold text-gray-900">Challenges Total</h3>
            </div>
            <p className="text-3xl font-bold text-blue-600">{globalStats.totalChallenges}</p>
            <p className="text-sm text-gray-600 mt-1">dont {globalStats.activeChallenges} actifs</p>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                <Users className="w-4 h-4 text-green-600" />
              </div>
              <h3 className="font-semibold text-gray-900">Participants</h3>
            </div>
            <p className="text-3xl font-bold text-green-600">{globalStats.totalParticipants}</p>
            <p className="text-sm text-gray-600 mt-1">commerciaux actifs</p>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-4 h-4 text-orange-600" />
              </div>
              <h3 className="font-semibold text-gray-900">Engagement</h3>
            </div>
            <p className="text-3xl font-bold text-orange-600">{globalStats.averageEngagement}%</p>
            <p className="text-sm text-gray-600 mt-1">participation moyenne</p>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                <Crown className="w-4 h-4 text-purple-600" />
              </div>
              <h3 className="font-semibold text-gray-900">Top Performer</h3>
            </div>
            <p className="text-lg font-bold text-purple-600">{globalStats.topPerformer}</p>
            <p className="text-sm text-gray-600 mt-1">+{globalStats.monthlyGrowth}% ce mois</p>
          </div>
        </div>

        {/* Gestion des challenges */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900">Challenges Actifs</h2>
            <div className="flex items-center gap-2">
              <button className="p-2 hover:bg-gray-100 rounded-lg">
                <BarChart3 className="w-4 h-4 text-gray-600" />
              </button>
              <button className="p-2 hover:bg-gray-100 rounded-lg">
                <Edit className="w-4 h-4 text-gray-600" />
              </button>
            </div>
          </div>

          <div className="space-y-4">
            {challenges.filter(c => c.status === 'active').map(challenge => (
              <div key={challenge.id} className="border border-gray-200 rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <Trophy className="w-6 h-6 text-yellow-500" />
                    <div>
                      <h3 className="font-semibold text-gray-900">{challenge.title}</h3>
                      <p className="text-sm text-gray-600">{challenge.description}</p>
                    </div>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(challenge.status)}`}>
                    {getStatusLabel(challenge.status)}
                  </span>
                </div>

                <div className="grid grid-cols-3 gap-4 mt-4">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-blue-600">{challenge.participants}</p>
                    <p className="text-xs text-gray-600">Participants</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-green-600">{Math.round((challenge.participants * 0.6))}%</p>
                    <p className="text-xs text-gray-600">Engagement</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-purple-600">{challenge.reward.split(' ')[1]}</p>
                    <p className="text-xs text-gray-600">Récompense</p>
                  </div>
                </div>

                {/* Top 3 du challenge */}
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Top 3 actuel :</h4>
                  <div className="flex items-center gap-4">
                    {challenge.leaderboard.slice(0, 3).map((participant, index) => (
                      <div key={`top3-${participant.name}-${index}`} className="flex items-center gap-2">
                        <span className="text-lg">{participant.avatar}</span>
                        <div>
                          <p className="text-sm font-medium text-gray-900">{participant.name}</p>
                          <p className="text-xs text-gray-600">
                            {participant.sales || participant.prospects} {challenge.id === 1 ? 'ventes' : 'prospects'}
                          </p>
                        </div>
                        {index === 0 && <Crown className="w-4 h-4 text-yellow-500" />}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Vue Commercial
  return (
    <div className="p-6 space-y-6">
      {/* Header Commercial */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-yellow-500 to-orange-600 rounded-xl flex items-center justify-center">
            <Trophy className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Mes Challenges</h1>
            <p className="text-gray-600">Relevez les défis et gagnez des récompenses !</p>
          </div>
        </div>
        
        <div className="flex items-center gap-4">
          <div className="text-sm text-gray-600">
            <span className="font-semibold">Rang général :</span> #2 🥈
          </div>
        </div>
      </div>

      {/* Mes performances */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-gradient-to-br from-yellow-50 to-orange-50 rounded-2xl border border-yellow-200 p-6">
          <div className="flex items-center gap-3 mb-3">
            <Flame className="w-6 h-6 text-orange-500" />
            <h3 className="font-semibold text-gray-900">Série Actuelle</h3>
          </div>
          <p className="text-3xl font-bold text-orange-600">7 jours</p>
          <p className="text-sm text-gray-600 mt-1">d'objectifs atteints</p>
        </div>

        <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl border border-green-200 p-6">
          <div className="flex items-center gap-3 mb-3">
            <CheckCircle className="w-6 h-6 text-green-500" />
            <h3 className="font-semibold text-gray-900">Challenges Gagnés</h3>
          </div>
          <p className="text-3xl font-bold text-green-600">3</p>
          <p className="text-sm text-gray-600 mt-1">ce trimestre</p>
        </div>

        <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-2xl border border-purple-200 p-6">
          <div className="flex items-center gap-3 mb-3">
            <Gift className="w-6 h-6 text-purple-500" />
            <h3 className="font-semibold text-gray-900">Récompenses</h3>
          </div>
          <p className="text-3xl font-bold text-purple-600">2 450€</p>
          <p className="text-sm text-gray-600 mt-1">de bonus gagnés</p>
        </div>
      </div>

      {/* Challenges actifs */}
      <div className="space-y-6">
        {challenges.map(challenge => (
          <div key={challenge.id} className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-xl flex items-center justify-center">
                  <Trophy className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">{challenge.title}</h3>
                  <p className="text-gray-600">{challenge.description}</p>
                </div>
              </div>
              
              <div className="text-right">
                <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(challenge.status)}`}>
                  {getStatusLabel(challenge.status)}
                </span>
                {challenge.myPosition && (
                  <div className="flex items-center gap-1 mt-2">
                    {getPositionIcon(challenge.myPosition)}
                    <span className="text-sm font-medium text-gray-700">#{challenge.myPosition}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Progression */}
            {challenge.status === 'active' && (
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">
                    Progression : {challenge.achieved} / {challenge.target}
                  </span>
                  <span className="text-sm font-bold text-gray-900">{challenge.myProgress}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div 
                    className={`h-3 rounded-full transition-all duration-500 ${getProgressColor(challenge.myProgress)}`}
                    style={{ width: `${challenge.myProgress}%` }}
                  ></div>
                </div>
              </div>
            )}

            {/* Récompense */}
            <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl p-4 mb-4">
              <div className="flex items-center gap-2 mb-1">
                <Gift className="w-4 h-4 text-orange-500" />
                <span className="text-sm font-medium text-gray-700">Récompense</span>
              </div>
              <p className="text-lg font-bold text-orange-700">{challenge.reward}</p>
            </div>

            {/* Classement */}
            {challenge.leaderboard && challenge.status === 'active' && (
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <BarChart3 className="w-4 h-4" />
                  Classement Actuel
                </h4>
                <div className="space-y-2">
                  {challenge.leaderboard.slice(0, 4).map((participant, index) => (
                    <div 
                      key={`leaderboard-${participant.name}-${index}`} 
                      className={`flex items-center justify-between p-3 rounded-lg ${
                        participant.name === 'Vous' ? 'bg-blue-50 border border-blue-200' : 'bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2">
                          {getPositionIcon(index + 1)}
                          <span className="font-medium text-gray-700">#{index + 1}</span>
                        </div>
                        <span className="text-lg">{participant.avatar}</span>
                        <span className={`font-medium ${participant.name === 'Vous' ? 'text-blue-900' : 'text-gray-900'}`}>
                          {participant.name}
                        </span>
                      </div>
                      <div className="text-right">
                        <span className="font-bold text-gray-900">
                          {participant.sales || participant.prospects}
                        </span>
                        <span className="text-sm text-gray-600 ml-1">
                          {challenge.id === 1 ? 'ventes' : 'prospects'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Période */}
            <div className="flex items-center gap-4 mt-4 pt-4 border-t border-gray-100">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Calendar className="w-4 h-4" />
                Du {new Date(challenge.startDate).toLocaleDateString('fr-FR')} 
                au {new Date(challenge.endDate).toLocaleDateString('fr-FR')}
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Users className="w-4 h-4" />
                {challenge.participants} participants
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Challenges;